import api from "./api";

// 3D Users service using backend endpoints
// GET    /users-3d?page&limit&search      -> { data: [...], meta: { total, page, limit, totalPages } }
// GET    /users-3d/linked-with/:id       -> { data: <3d user> }
// POST   /users-3d                       -> { id, name, email, company_id, created_at }  (created_by auto from JWT)
// PUT    /users-3d/:id                   -> updated user (updated_at handled by database)
// DELETE /users-3d/:id                   -> { ok: true }

const normalize3DUser = (u) => {
  if (!u || typeof u !== "object") return u;
  const email = u.email ?? u.user_email ?? "";
  const name = u.name ?? u.username ?? u.full_name ?? "";
  const created_by =
    u.created_by ?? u.createdBy ?? u.created_user_id ?? u.createdUserId ?? null;
  const created_at = u.created_at ?? u.createdAt ?? null;
  const updated_at = u.updated_at ?? u.updatedAt ?? null;
  const company_id = u.company_id ?? u.companyId ?? null;
  // user_id is the linked CMS user ID (nullable)
  const user_id = u.user_id ?? u.userId ?? u.linked_user_id ?? null;
  return {
    ...u,
    email,
    name,
    created_by,
    created_at,
    updated_at,
    company_id,
    user_id,
  };
};

export async function get3DUsers({ page = 1, limit = 10, search = "" } = {}) {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("limit", String(limit));
  if (search && search.trim()) params.set("search", search.trim());
  const res = await api.get(`/users-3d?${params.toString()}`);
  // Handle paginated { data, meta } shape
  if (res && Array.isArray(res.data)) {
    return {
      data: res.data.map(normalize3DUser),
      meta: res.meta || { total: res.data.length, page, limit, totalPages: 1 },
    };
  }
  // Fallback: plain array
  const arr = Array.isArray(res) ? res : [];
  return {
    data: arr.map(normalize3DUser),
    meta: {
      total: arr.length,
      page: 1,
      limit: arr.length || 10,
      totalPages: 1,
    },
  };
}

export async function get3DUsersBasicInfo({ search = "" } = {}) {
  const params = new URLSearchParams();
  if (search && search.trim()) params.set("search", search.trim());

  const qs = params.toString();
  const res = await api.get(`/users-3d/basic-info${qs ? `?${qs}` : ""}`);

  const data =
    Array.isArray(res?.data) ? res.data
    : Array.isArray(res) ? res
    : [];

  return data.map(normalize3DUser);
}

// Fetch linked 3D user for a given CMS user id
export async function getLinked3DUser(cmsUserId) {
  if (cmsUserId === undefined || cmsUserId === null || cmsUserId === "") {
    throw new Error("userId is required");
  }
  const idNum = Number(cmsUserId);
  if (!Number.isInteger(idNum)) {
    throw new Error("userId must be an integer");
  }
  const res = await api.get(`/users-3d/linked-with/${idNum}`);
  const data = res?.data ?? null;
  return data ? normalize3DUser(data) : null;
}

export async function create3DUser(payload) {
  // created_by is auto-injected from JWT on the backend
  const body = {
    name: payload.name,
    email: payload.email,
    company_id: payload.company_id ?? null,
    password: payload.password,
    confirmPassword: payload.confirmPassword,
  };
  return api.post("/users-3d", body);
}

export async function update3DUser(userId, payload) {
  const idNum = Number(userId);
  if (!Number.isInteger(idNum)) {
    throw new Error("userId must be an integer");
  }
  const body = { ...payload };
  // Do NOT send the id in the body
  delete body.id;
  delete body.user_3d_id;
  // Do NOT send updated_at, let the database handle it
  delete body.updated_at;
  delete body.created_at;
  delete body.created_by;
  return api.put(`/users-3d/${idNum}`, body);
}

// Alias for backward compatibility
export const patch3DUser = update3DUser;

// Reset password for a linked 3D user via PUT
export async function reset3DUserPassword(user3dId, payload) {
  const idNum = Number(user3dId);
  if (!Number.isInteger(idNum)) {
    throw new Error("user3dId must be an integer");
  }
  const body = {
    password: payload.password,
    confirmPassword: payload.confirmPassword,
  };
  return api.put(`/users-3d/${idNum}`, body);
}

/**
 * Link a CMS user to a 3D user
 * @param {number} user3dId - The 3D user ID
 * @param {number|null} cmsUserId - The CMS user ID to link, or null to unlink
 */
export async function link3DUser(user3dId, cmsUserId) {
  const idNum = Number(user3dId);
  if (!Number.isInteger(idNum)) {
    throw new Error("user3dId must be an integer");
  }
  const body = { user_id: cmsUserId };
  const result = await api.put(`/users-3d/${idNum}`, body);
  return normalize3DUser(result);
}

export async function delete3DUser(userId) {
  if (userId === undefined || userId === null || userId === "") {
    throw new Error("userId is required");
  }
  const idNum = Number(userId);
  if (!Number.isInteger(idNum) || isNaN(idNum)) {
    throw new Error(
      `userId must be an integer, received: ${userId} (type: ${typeof userId})`,
    );
  }
  return api.delete(`/users-3d/${idNum}`);
}
